import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { GoldButton, SectionHeading } from "@/components/Brand";

const field = "min-h-[48px] w-full border border-[#C69A3B]/60 bg-white px-4 text-base";

export default function AuthPage({ mode = "login" }) {
  const { t, path } = useI18n();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register({ email: form.email, password: form.password, name: form.name });
      navigate(path("account"));
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + path("account");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16 sm:px-8 lg:py-24" data-testid={`${mode}-page`}>
      <SectionHeading label="HR Creations" title={isLogin ? t("auth.loginTitle") : t("auth.registerTitle")} />
      <form onSubmit={submit} className="mt-10 space-y-5" data-testid={`${mode}-form`}>
        {!isLogin && (
          <div>
            <label className="label-mono mb-2 block" htmlFor="a-name">{t("auth.name")}</label>
            <input id="a-name" required data-testid="auth-name" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={field} />
          </div>
        )}
        <div>
          <label className="label-mono mb-2 block" htmlFor="a-email">{t("auth.email")}</label>
          <input id="a-email" type="email" required data-testid="auth-email" value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={field} />
        </div>
        <div>
          <label className="label-mono mb-2 block" htmlFor="a-password">{t("auth.password")}</label>
          <input id="a-password" type="password" required minLength={isLogin ? undefined : 8} data-testid="auth-password"
            value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={field} />
        </div>
        {error && (
          <p className="text-sm text-[#8F3329]" data-testid="auth-error">
            {error}
          </p>
        )}
        <GoldButton type="submit" disabled={busy} className="w-full" data-testid="auth-submit">
          {busy ? t("common.loading") : isLogin ? t("btn.login") : t("btn.register")}
        </GoldButton>
      </form>

      <div className="my-7 flex items-center gap-3 text-xs text-[#5A5752]">
        <span className="gold-rule flex-1" /> {t("auth.or")} <span className="gold-rule flex-1" />
      </div>
      <GoldButton variant="outline" className="w-full" onClick={googleLogin} data-testid="auth-google-btn">
        {t("btn.google")}
      </GoldButton>

      <p className="mt-8 text-sm text-[#5A5752]">
        {isLogin ? t("auth.noAccount") : t("auth.hasAccount")}{" "}
        <Link
          to={isLogin ? path("register") : path("login")}
          data-testid="auth-switch-link"
          className="text-[#8F3329] underline"
        >
          {isLogin ? t("btn.register") : t("btn.login")}
        </Link>
      </p>
    </div>
  );
}
